// useOpenNotification — переход по клику на уведомление.
//
// Раньше клик ВСЕГДА вёл на страницу самого уведомления (`/notifications/$id`),
// что не совпадало с ожиданием пользователя: тапнув «Запись обработана», он хочет
// попасть на саму запись, а не на карточку уведомления. Теперь переход идёт на
// связанную сущность через `actionUrl` (canonical route артефакта, который
// сервер кладёт в notifyArtifactReady). Уведомления без `actionUrl` (общие) —
// fallback на деталь `/notifications/$id`.
//
// Клик также отмечает уведомление прочитанным (PowerSync local UPDATE, offline-safe,
// идемпотентно через `WHERE read_at IS NULL`) — раньше это делал только useEffect
// на странице детали, куда мы теперь чаще не заходим.

import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { isExternalUrl, parseActionUrl } from "@/lib/action-url";
import { getPowerSync } from "@/lib/powersync/client";

interface OpenableNotification {
	id: string;
	actionUrl?: string | null;
	read: boolean;
}

export function useOpenNotification() {
	const navigate = useNavigate();

	return useCallback(
		(item: OpenableNotification) => {
			// Отметить прочитанным локально (синкается на сервер через write-queue).
			if (!item.read) {
				void getPowerSync().execute(
					`UPDATE notifications SET read_at = ? WHERE id = ? AND read_at IS NULL`,
					[new Date().toISOString(), item.id],
				);
			}

			const url = item.actionUrl;
			if (url) {
				// Внешний URL → полная навигация; внутренний путь → SPA-навигация.
				if (isExternalUrl(url)) {
					window.location.href = url;
				} else {
					// `?view=` / `#chunk-` разбираем ДО navigate: внутри `to` роутер их
					// не парсит и склеил бы query с именем пути (см. lib/action-url.ts).
					void navigate({ ...parseActionUrl(url) });
				}
				return;
			}

			// Нет связанной сущности — открыть деталь уведомления.
			void navigate({
				to: "/notifications/$id",
				params: { id: item.id },
			});
		},
		[navigate],
	);
}
